// This page displays all available integrations and talk-to-text functionality
// Updated to include talk-to-text UI and improved Google account connection prompt
import { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { GoogleDriveIntegration } from '@/components/integrations/GoogleDriveIntegration'
import { GoogleDriveFileBrowser } from '@/components/integrations/GoogleDriveFileBrowser'
import { TikTokScraperIntegration } from '@/components/integrations/TikTokScraperIntegration'
import { Separator } from '@/components/ui/separator'
import { FileText, Settings, Mic, AlertCircle, Video } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

export default function IntegrationsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('google-drive')
  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [hasGoogleAccount, setHasGoogleAccount] = useState(false)
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login')
    }
  }, [user, navigate])
  
  // Check if user has connected Google account
  useEffect(() => {
    // This is a placeholder - in a real implementation, you would check
    // if the user has connected their Google account from your database
    const checkGoogleConnection = async () => {
      if (user) {
        try {
          // Example: Check if user has a Google connection in your database
          // const { data } = await supabase
          //   .from('google_drive_connections')
          //   .select('id')
          //   .eq('user_id', user.id)
          //   .single();
          
          // setHasGoogleAccount(!!data);
          
          // For now, we'll just set it to false for demonstration
          setHasGoogleAccount(false)
        } catch (error) {
        }
      }
    }
    
    checkGoogleConnection()
  }, [user])
  
  // Talk-to-text functionality
  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser. Please try Chrome.')
      return
    }
    
    const SpeechRecognition = window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    
    recognition.continuous = true
    recognition.interimResults = true
    
    recognition.onstart = () => {
      setIsRecording(true)
    }
    
    recognition.onresult = (event) => {
      let interimTranscript = ''
      let finalTranscript = ''
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += transcript
        } else {
          interimTranscript += transcript
        }
      }
      
      setTranscript(finalTranscript || interimTranscript)
    }
    
    recognition.onerror = (event) => {
      setIsRecording(false)
    }
    
    recognition.onend = () => {
      setIsRecording(false)
    }
    
    recognition.start()
    
    // Store the recognition instance to stop it later
    window.currentRecognition = recognition
  }
  
  const stopRecording = () => {
    if (window.currentRecognition) {
      window.currentRecognition.stop()
    }
    setIsRecording(false)
  }
  
  if (!user) {
    return null
  }
  
  return (
    <div className="container py-10">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
            <p className="text-muted-foreground mt-1">
              Connect external services to enhance your Posit experience
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/posits')}>
            Back to Posits
          </Button>
        </div>
        
        <Separator />
        
        {!hasGoogleAccount && (
          <Alert variant="warning" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Google Account Required</AlertTitle>
            <AlertDescription>
              To use the Google Drive integration, you need to connect your Google account.
              Click the "Connect Google Drive" button below to get started.
            </AlertDescription>
          </Alert>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="google-drive" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Google Drive
            </TabsTrigger>
            <TabsTrigger value="tiktok" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              TikTok
            </TabsTrigger>
            <TabsTrigger value="talk-to-text" className="flex items-center gap-2">
              <Mic className="h-4 w-4" />
              Talk to Text
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="google-drive" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <GoogleDriveIntegration />
              <Card>
                <CardHeader>
                  <CardTitle>About Google Drive Integration</CardTitle>
                  <CardDescription>
                    How the Google Drive integration works with Posit
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    The Google Drive integration allows you to import documents from your Google Drive directly into Posit.
                    This is useful for creating Posits from existing documents without having to copy and paste content.
                  </p>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Supported File Types:</h3>
                    <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                      <li>Google Docs</li>
                      <li>Google Sheets (as CSV)</li>
                      <li>Text files (.txt)</li>
                      <li>Markdown files (.md)</li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    When you import a file, Posit creates a new document with the content from your Google Drive file.
                    The original file in Google Drive remains unchanged.
                  </p>
                </CardContent>
              </Card>
            </div>

            <GoogleDriveFileBrowser />
          </TabsContent>

          <TabsContent value="tiktok" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TikTokScraperIntegration />
              <Card>
                <CardHeader>
                  <CardTitle>About TikTok Integration</CardTitle>
                  <CardDescription>
                    Import your TikTok content to create Posits quickly
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    The TikTok integration allows you to scrape and import your TikTok profile data, videos, and hashtags directly into Posit.
                    This is perfect for creators who want to quickly build their knowledge base from existing TikTok content.
                  </p>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">What you can import:</h3>
                    <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                      <li>Profile information and bio</li>
                      <li>Video content and descriptions</li>
                      <li>Hashtag-based content</li>
                      <li>Engagement metrics and timestamps</li>
                      <li>Music and video metadata</li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Once imported, your TikTok data becomes part of your Posit, making it searchable and available for AI-powered conversations.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="talk-to-text" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Talk to Text</CardTitle>
                <CardDescription>
                  Convert your speech to text for use in Posits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border rounded-md min-h-[200px] bg-muted/30">
                  {transcript ? (
                    <p>{transcript}</p>
                  ) : (
                    <p className="text-muted-foreground text-center">
                      {isRecording ? "Listening..." : "Click the microphone button to start speaking"}
                    </p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                {isRecording ? (
                  <Button variant="destructive" onClick={stopRecording}>
                    <Mic className="h-4 w-4 mr-2" />
                    Stop Recording
                  </Button>
                ) : (
                  <Button onClick={startRecording}>
                    <Mic className="h-4 w-4 mr-2" />
                    Start Recording
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (transcript) {
                      navigator.clipboard.writeText(transcript)
                      alert('Transcript copied to clipboard!')
                    }
                  }}
                  disabled={!transcript}
                >
                  Copy to Clipboard
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Integration Settings</CardTitle>
                <CardDescription>
                  Manage your integration preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No additional settings are available for integrations at this time.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
} 

// Add TypeScript declaration for the SpeechRecognition API
declare global {
  interface Window {
    webkitSpeechRecognition: any;
    currentRecognition: any;
  }
} 